import { dispatchSessionExpired, getValidJwtToken } from "../auth/authSession";
import { getApiBaseUrl } from "../auth/apiBase";

// Shape returned by GET /api/properties
export type RemotePropertyOption = {
  id: string;
  label: string;
  code: string;
  isDefault: boolean;
};

type PropertiesApiResponse = {
  ok: boolean;
  properties?: unknown[];
  error?: string;
};

function resolveApiBase(): string {
  return getApiBaseUrl();
}

function isRemotePropertyOption(value: unknown): value is RemotePropertyOption {
  if (!value || typeof value !== "object") return false;
  const p = value as Record<string, unknown>;
  return typeof p.id === "string" && p.id.trim() !== "" && typeof p.label === "string";
}

/**
 * Fetches the list of properties (hotels) that the current JWT user can see.
 * Requires a valid JWT token — returns an empty array when none is present.
 * Throws on network/server errors so callers can decide how to fall back.
 */
export async function fetchProperties(): Promise<RemotePropertyOption[]> {
  const token = getValidJwtToken();
  if (!token) return [];

  const url = `${resolveApiBase()}/api/properties`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (networkError) {
    throw new Error(
      `Network error loading properties: ${networkError instanceof Error ? networkError.message : String(networkError)}`,
    );
  }

  if (response.status === 401) {
    dispatchSessionExpired();
    throw new Error("Session expired while fetching properties.");
  }

  if (!response.ok) {
    throw new Error(`Properties endpoint returned HTTP ${response.status}.`);
  }

  let payload: PropertiesApiResponse;
  try {
    payload = (await response.json()) as PropertiesApiResponse;
  } catch {
    throw new Error("Properties endpoint returned non-JSON response.");
  }

  if (!payload.ok || !Array.isArray(payload.properties)) {
    throw new Error(payload.error ?? "Unexpected shape in properties response.");
  }

  return payload.properties.filter(isRemotePropertyOption);
}
